import * as O from "fp-ts/lib/Option";
import * as io from "io-ts";
import { useRouter } from "next/router";
import { defineMessage, type IntlShape } from "react-intl";
import type * as ta from "type-assertions";
import { focusArticles } from "./focus";
import type { Locale } from "./locales";

// -----------------------------------------------------------------------------
// Route Aliases

const pageAliasesK = {
  home: null,
  terms: null,
  about: null,
  indicators: null,
  embed: null,
  figure: null,
  figures: null,
  markdown: null,
  focusPosts: null,
};

const articleAliasesK = {
  access: null,
  equity: null,
  learning: null,
  trajectories: null,
  quality: null,
  finance: null,
  "finance-aid": null,
};

const themesAliasesK = {
  access: null,
  equity: null,
  learning: null,
  quality: null,
  finance: null,
};

const focusAliasesK = focusArticles.published.reduce((acc, curr) => ({ ...acc, [curr]: null }), {});

export const RAlias = io.keyof({
  ...pageAliasesK,
  ...articleAliasesK,
  ...focusAliasesK,
});

export const aliasFromRouter = ({ pathname, query }: { pathname: string; query: any }) => {
  if (pathname === "/") {
    return "home";
  } else if (pathname.startsWith("/articles/")) {
    return query.article;
  } else if (pathname === "/focus-articles" || pathname.startsWith("/focus/")) {
    return "focusPosts";
  } else {
    return pathname.split("/").pop();
  }
};

export function useAlias(): RAlias {
  const router = useRouter();

  if (router.asPath === "/") {
    return "home";
  } else if (router.asPath === "/focus-articles") {
    return "focusPosts";
  } else if (router.pathname === "/articles/[article]") {
    return router.query.article as RAlias;
  } else if (router.pathname === "/focus/[focus]") {
    return router.query.focus as RAlias;
  } else {
    return router.asPath.split("/").pop() as RAlias;
  }
}

export type RAlias = io.TypeOf<typeof RAlias>;

export const RAliasArticle = io.keyof(articleAliasesK);
export type RAliasArticle = io.TypeOf<typeof RAliasArticle>;
export const articleAliases = Object.keys(RAliasArticle.keys) as Array<RAliasArticle>;

export const RAliasTheme = io.keyof(themesAliasesK);
export type RAliasTheme = io.TypeOf<typeof RAliasTheme>;
export const themeAliases = Object.keys(RAliasTheme.keys) as Array<RAliasTheme>;

export const subthemesIndex: Record<RAliasTheme, RAliasArticle[]> = {
  access: ["access"],
  equity: ["equity"],
  learning: ["learning", "trajectories"],
  quality: ["quality"],
  finance: ["finance", "finance-aid"],
};

const foldRAliasArticle = <A>(alias: RAliasArticle, pattern: { [K in RAliasArticle]: () => A }): A => {
  return pattern[alias]();
};

export const foldRAliasArticle_ =
  <A>(pattern: { [K in RAliasArticle]: () => A }) =>
  (alias: RAliasArticle) =>
    foldRAliasArticle(alias, pattern);

const foldRAliasTheme = <A>(alias: RAliasTheme, pattern: { [K in RAliasTheme]: () => A }): A => {
  return pattern[alias]();
};
export const foldRAliasTheme_ =
  <A>(pattern: { [K in RAliasTheme]: () => A }) =>
  (alias: RAliasTheme) =>
    foldRAliasTheme(alias, pattern);

export function findArticleAlias(route: RAlias): O.Option<RAliasArticle> {
  return O.fromPredicate<RAliasArticle>((x) => articleAliases.includes(x))(route as RAliasArticle);
}

export function findArticleThemeAlias(article: RAliasArticle): RAliasTheme {
  const themes = Object.keys(subthemesIndex) as Array<RAliasTheme>;
  return themes.find((t) => subthemesIndex[t].includes(article)) as RAliasTheme;
}

export function findArticleAliasRelaxed(route?: string): O.Option<RAliasArticle> {
  return O.fromPredicate<RAliasArticle>((x) => articleAliases.includes(x))(route as RAliasArticle);
}

export function findThemeAliasIndex(themeAlias: RAliasTheme) {
  return themeAliases.findIndex((x) => x === themeAlias);
}

export function getArticleName(intl: IntlShape, alias: RAliasArticle) {
  return foldRAliasArticle(alias, {
    access: () => intl.formatMessage(defineMessage({ id: "Access", defaultMessage: "Access" })),
    equity: () => intl.formatMessage(defineMessage({ id: "Equity", defaultMessage: "Equity" })),
    learning: () => intl.formatMessage(defineMessage({ id: "Learning outcomes", defaultMessage: "Learning outcomes" })),
    trajectories: () =>
      intl.formatMessage(defineMessage({ id: "Learning trajectories", defaultMessage: "Learning trajectories" })),
    quality: () => intl.formatMessage(defineMessage({ id: "Quality", defaultMessage: "Quality" })),
    finance: () =>
      intl.formatMessage(
        defineMessage({
          id: "Finance – Governments and households",
          defaultMessage: "Finance – Governments and households",
        }),
      ),
    "finance-aid": () => intl.formatMessage(defineMessage({ id: "Finance – Aid", defaultMessage: "Finance – Aid" })),
  });
}

// -----------------------------------------------------------------------------
// Route definition

export const routes = {
  home: { publicPath: formatPublicPathL("/index") },
  about: { publicPath: formatPublicPathL("/about") },
  terms: { publicPath: formatPublicPathL("/terms") },
  indicators: { publicPath: formatPublicPathL("/indicators") },
  focusPosts: { publicPath: formatPublicPathL("/focus-articles") },
  embed: {
    publicPath: formatPublicPathL("/embed"),
  },
  figure: {
    publicPath: formatPublicPathL("/figure"),
  },
  figures: {
    publicPath: formatPublicPathL("/figures"),
  },
  markdown: {
    publicPath: formatPublicPathL("/_markdown"),
  },

  access: { publicPath: formatPublicPathL("/articles/access") },
  equity: { publicPath: formatPublicPathL("/articles/equity") },
  learning: { publicPath: formatPublicPathL("/articles/learning") },
  trajectories: { publicPath: formatPublicPathL("/articles/trajectories") },
  quality: { publicPath: formatPublicPathL("/articles/quality") },
  finance: { publicPath: formatPublicPathL("/articles/finance") },
  "finance-aid": { publicPath: formatPublicPathL("/articles/finance-aid") },
};

const NOINDEX_ROUTES = ["/figures", "/_markdown"];
export const isNoIndexRoute = (route: string) => NOINDEX_ROUTES.includes(route);

// Typelevel assertion to ensure all routes defined in RAlias are actually
// implemented. We use this because we can't infer the value-side of objects
// that are not homogenous.
export type assertRoutes1 = ta.Assert<ta.UnionIncludesExact<RAlias, keyof typeof routes>>;
export type assertRoutes2 = ta.Assert<ta.UnionIncludesExact<keyof typeof routes, RAlias>>;

/**
 * Get the Next.js Link href for any route alias (without locale prefix).
 * For focus articles (not in the static routes), returns /focus/<alias>.
 */
export function getAliasHref(alias: RAlias): string {
  const routeDef = routes[alias as keyof typeof routes];
  if (routeDef) {
    // publicPath("en") = "/en/<path>"; strip the locale prefix (first path segment) to get "/<path>"
    return routeDef.publicPath("en").replace(/^\/[^/]+/, "") || "/";
  }
  // Focus article
  return `/focus/${alias}`;
}

// -----------------------------------------------------------------------------
// Formatters

function formatPublicPathL(path: string) {
  return (locale: Locale) => `/${locale}${path === "/index" ? "" : path}`;
}
